import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";
import { constants } from "../utils/ranges";
import LoadingScreen from "../components/LoadingScreen";
import { ReactComponent as BackArrow } from "../assets/icons/BackArrow.svg";
import { ReactComponent as Arrow } from "../assets/icons/Arrow.svg";
import BloodPressureScore from "../assets/images/scores/BloodPressure.png";
import HeartHealthScore from "../assets/images/scores/HeartHealth.png";
import MentalWellbeingScore from "../assets/images/scores/MentalWellbeing.png";
import PhysiologicalScore from "../assets/images/scores/Physiological.png";

const relationIcons = {
  Self: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Father: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Mother: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Spouse: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  default: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
};

const getRelationIcon = (relation) => {
  return relationIcons[relation] || relationIcons.default;
};

const genderColors = {
  male: "from-[#1845a3] to-[#3B82F6]",
  female: "from-[#EC4899] to-[#ED1C24]",
  others: "from-[#8B5CF6] to-[#6366F1]",
};

const Scores = ({ list = [] }) => {
  return (
    <div className="space-y-3">
      {list.map((score, index) => {
        const selectedScoreRange =
          constants["Scores"].ranges[
            score.title === "Blood Pressure"
              ? score.value < 108 || score.value > 135
                ? 2
                : score.value < 115 || score.value > 125
                ? 1
                : 0
              : score.value >= 75
              ? 0
              : score.value >= 50
              ? 1
              : 2
          ];
        return (
          <div key={`score-${index}`} className="flex items-center">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center shadow-sm">
              <img className="h-4 w-4 brightness-0 invert" src={score.logo} alt={score.title} />
            </div>
            <h2 className="grow mx-2.5 text-xs font-semibold text-gray-800 leading-tight">{score.title}</h2>
            <div className="shrink-0 flex items-center space-x-1">
              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: selectedScoreRange.color }} />
              <span className="text-xxs font-semibold" style={{ color: selectedScoreRange.color }}>
                {selectedScoreRange.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const FamilyTreeSection = ({ agentCode }) => {
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!agentCode) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`/api/family-scans/by-referral/${encodeURIComponent(agentCode)}`);
        const data = await resp.json();
        if (data.success) {
          setFamilyData(data.data);
        } else {
          setError(data.message || "Failed to fetch family data.");
        }
      } catch (err) {
        setError("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [agentCode]);

  if (!agentCode) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-[#1845a3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl bg-red-50 border border-red-100">
        <p className="text-xs text-red-600">{error}</p>
      </div>
    );
  }

  if (!familyData || familyData.total_scans === 0) {
    return (
      <div className="py-8 text-center">
        <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#1845a3]/10 to-[#ED1C24]/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-[#1845a3]/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-400">No family scans recorded yet</p>
        <p className="text-xxs text-gray-300 mt-1">Scans will appear here once completed</p>
      </div>
    );
  }

  const families = Object.entries(familyData.families);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-800">{familyData.family_count} {familyData.family_count === 1 ? "Family" : "Families"}</p>
            <p className="text-xxs text-gray-400">{familyData.total_scans} total scans</p>
          </div>
        </div>
        <div className="px-3 py-1 rounded-full bg-gradient-to-r from-[#1845a3]/10 to-[#ED1C24]/10">
          <span className="text-xxs font-semibold bg-gradient-to-r from-[#1845a3] to-[#ED1C24] bg-clip-text text-transparent">{familyData.referral_code}</span>
        </div>
      </div>

      {families.map(([phone, scans], familyIdx) => {
        const selfScan = scans.find(s => s.relation === "Self");
        const otherScans = scans.filter(s => s.relation !== "Self");

        return (
          <div key={phone} className="rounded-2xl overflow-hidden bg-white shadow-md border border-gray-100">
            <div className="bg-gradient-to-r from-[#1845a3]/5 to-[#ED1C24]/5 px-4 py-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="w-3.5 h-3.5 text-[#1845a3]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-xs font-bold text-gray-700">{selfScan?.person_name || scans[0]?.person_name || "Customer"}</span>
                </div>
                <span className="text-xxs text-gray-400 font-medium">{new Date(scans[scans.length - 1]?.created_at || scans[0]?.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}</span>
              </div>
            </div>

            <div className="p-4">
              <div className="flex flex-col items-center">
                {selfScan && (
                  <div className="flex flex-col items-center mb-2">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${genderColors[selfScan.gender] || "from-[#1845a3] to-[#ED1C24]"} flex items-center justify-center shadow-lg ring-2 ring-white`}>
                      <span className="text-white text-sm font-bold">
                        {selfScan.person_name ? selfScan.person_name.charAt(0).toUpperCase() : "S"}
                      </span>
                    </div>
                    <div className="mt-1.5 text-center">
                      <p className="text-xs font-bold text-gray-800">{selfScan.person_name || "Self"}</p>
                      <p className="text-xxs text-gray-400">
                        {selfScan.gender ? selfScan.gender.charAt(0).toUpperCase() + selfScan.gender.slice(1) : ""}
                      </p>
                      <p className="text-xxs text-gray-300 mt-0.5">
                        {new Date(selfScan.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })}
                      </p>
                    </div>
                  </div>
                )}

                {otherScans.length > 0 && (
                  <>
                    {selfScan && (
                      <div className="w-px h-4 bg-gradient-to-b from-[#1845a3] to-[#ED1C24]" />
                    )}

                    {otherScans.length > 1 && (
                      <div className="relative w-full">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px bg-gradient-to-r from-[#1845a3]/30 via-[#ED1C24]/30 to-[#1845a3]/30" style={{ width: `${Math.min(90, otherScans.length * 25)}%` }} />
                      </div>
                    )}

                    <div className={`flex flex-wrap justify-center gap-4 ${selfScan ? "mt-2" : ""}`}>
                      {otherScans.map((scan, idx) => (
                        <div key={scan.id || idx} className="flex flex-col items-center">
                          {selfScan && otherScans.length > 1 && (
                            <div className="w-px h-3 bg-gradient-to-b from-[#1845a3]/30 to-[#ED1C24]/30" />
                          )}
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${genderColors[scan.gender] || "from-[#1845a3] to-[#ED1C24]"} flex items-center justify-center shadow-md ring-2 ring-white`}>
                            <span className="text-white text-xs font-bold">
                              {scan.person_name ? scan.person_name.charAt(0).toUpperCase() : scan.relation?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <div className="mt-1 text-center max-w-[72px]">
                            <p className="text-xxs font-semibold text-gray-700 truncate">{scan.person_name || scan.relation}</p>
                            <div className="flex items-center justify-center space-x-0.5 mt-0.5">
                              <span className="text-white">{getRelationIcon(scan.relation)}</span>
                              <span className="text-xxs text-gray-400">{scan.relation}</span>
                            </div>
                            <p className="text-xxs text-gray-300 mt-0.5">
                              {new Date(scan.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const History = () => {
  const {
    getToken,
    userData: { employee_id },
    agentInfo,
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [activeTab] = useState("family");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const scoreResp = await fetch(sdkWrapperURL("/users/sdk/test_api_private"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            action: "list_scan_UL",
            employee_id,
            table_config_rows_per_page: 99,
            table_config_page_no: 1,
          }),
          signal: controller.signal,
        });
        const scoreRespJSON = await scoreResp.json();
        if (scoreRespJSON?.statusCode?.toString().startsWith("2") && scoreRespJSON?.result?.length > 0)
          setResults(scoreRespJSON.result);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#1845a3] to-[#ED1C24] px-5 pt-5 pb-6">
        <div className="flex items-center">
          <Link className="flex-shrink-0 p-1 pr-3" to={-1}>
            <BackArrow className="h-2.5 w-2.5 text-white" />
          </Link>
          <h3 className="text-sm font-semibold text-white tracking-wide">Assessment History</h3>
        </div>
        {agentInfo?.code && (
          <div className="mt-3 flex items-center space-x-2">
            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-white text-xxs font-bold">{agentInfo.name?.charAt(0)?.toUpperCase() || "A"}</span>
            </div>
            <div>
              <p className="text-white text-xxs font-medium">{agentInfo.name}</p>
              <p className="text-white/60 text-xxs">{agentInfo.code}</p>
            </div>
          </div>
        )}
      </div>

      <div className="px-5 -mt-3">
        <div className="flex rounded-xl overflow-hidden bg-white shadow-md border border-gray-100">
          <div className="flex-1 py-2.5 text-xs font-semibold text-center bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white">
            Family Scans
          </div>
        </div>
      </div>

      <div className="px-5 py-4">
        {activeTab === "family" ? (
          <FamilyTreeSection agentCode={agentInfo?.code} />
        ) : loading ? (
          <LoadingScreen style={{ minHeight: "40vh" }} />
        ) : results.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#1845a3]/10 to-[#ED1C24]/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#1845a3]/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-400">No assessment history found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {results.map((r) => {
              const date = new Date(r.created_at?.replace(" ", "T") || 0);
              return (
                <div key={r.scan_id} className="rounded-2xl overflow-hidden bg-white shadow-md border border-gray-100">
                  <div className="bg-gradient-to-r from-[#1845a3] to-[#ED1C24] px-4 py-2.5">
                    <p className="text-white text-xxs font-medium">
                      {date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" })}
                    </p>
                  </div>
                  <div className="p-4">
                    <Scores
                      list={[
                        { title: "Blood Pressure", value: r.vitals.bp_sys, logo: BloodPressureScore },
                        { title: "Heart Health", value: r.overall_heart_score, logo: HeartHealthScore },
                        { title: "Mental Wellbeing", value: r.mental_wellbeing_score, logo: MentalWellbeingScore },
                        { title: "Physiological", value: r.physiological_score, logo: PhysiologicalScore },
                      ]}
                    />
                    <Link
                      className="mt-4 flex items-center justify-center space-x-2 py-2.5 rounded-xl bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white"
                      to="/scan-result"
                      state={{
                        fromHistory: true,
                        result: {
                          entry_time: r.created_at,
                          scan_id: r.scan_id,
                          posture: r.posture,
                          wellness_score: r.wellness_score,
                          vitals: r.vitals,
                          metadata: {
                            heart_scores: r.heart_scores,
                            cardiovascular: r.cardiovascular,
                            physiological_scores: r.physiological_scores,
                            mental_wellbeing_score: r.mental_wellbeing_score,
                            overall_heart_score: r.overall_heart_score,
                            physiological_score: r.physiological_score,
                          },
                          notes: r.notes,
                           scan_duration: r.scan_duration ?? r.notes?.scan_duration_seconds,
                        },
                      }}
                    >
                      <span className="text-xs font-semibold">View Result</span>
                      <Arrow className="shrink-0 h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default History;
